import {Button, Col, Container, FloatingLabel, Form, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {deleteCoursePrice, getCoursePriceList, upsertCoursePrice} from "../api/CourseApi";
import {toolTipRenderer} from "../utils/gridUtils";
import {centsToDollarStr, centsToDollarStrWSign, dollarsToCents, getConfLabel, showSuccessToast, titleCase}
  from "../utils/usacmUtils";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {
  CONF_LABEL_KEY_COURSE,
  COURSE_ACCESS_TYPE_DAYS,
  COURSE_ACCESS_TYPE_END_DATE,
  COURSE_ACCESS_TYPE_NAMES,
  COURSE_ACCESS_TYPE_PERPETUAL,
  COURSE_ACCESS_TYPES
} from "../constants";

/**
 * The price options of one course (member / non-member / student ...), and the dialog to add or edit one.
 * Buying a price is what grants access, so each price says how long that access lasts.  A price of 0 is free -
 * the user enrolls without paying.  Amounts are stored in cents and edited in dollars.  A price which requires review makes a purchase wait for an
 * admin to approve it, and requires file has the buyer upload proof (eg. a student id) for that review.
 * A price which is on an order can't be deleted - the server refuses, and it should be made inactive instead.
 */
export function CoursePricing({course, canEdit, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [priceList, setPriceList] = useState([]);
  const [errors, setErrors] = useState([]);
  // The price in the add/edit dialog - {} while adding a new one, null when the dialog is closed
  const [editingPrice, setEditingPrice] = useState(null);
  const [deletingPrice, setDeletingPrice] = useState(null);
  // Add/edit dialog fields
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editAccessType, setEditAccessType] = useState(COURSE_ACCESS_TYPE_PERPETUAL);
  const [editAccessDays, setEditAccessDays] = useState('');
  const [editAccessEndDate, setEditAccessEndDate] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editRequiresReview, setEditRequiresReview] = useState(false);
  const [editRequiresFile, setEditRequiresFile] = useState(false);
  const [editReviewInstructions, setEditReviewInstructions] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editPriceOrder, setEditPriceOrder] = useState('');
  const [editErrors, setEditErrors] = useState([]);
  const gridRef = useRef();
  const hasFewRows = (priceList || []).length < 8;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const isAdding = !!editingPrice && !editingPrice.id;

  useEffect(() => {
    if (!course?.id) {
      return;
    }
    getCoursePriceList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setPriceList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }, [course?.id]);

  function startEditingPrice(coursePrice) {
    setEditName(coursePrice.name || '');
    setEditDescription(coursePrice.description || '');
    setEditAmount(coursePrice.id ? centsToDollarStr(coursePrice.amount) : '');
    setEditAccessType(coursePrice.course_access_type || COURSE_ACCESS_TYPE_PERPETUAL);
    setEditAccessDays(coursePrice.access_days || '');
    setEditAccessEndDate(coursePrice.access_end_date || '');
    setEditStartDate(coursePrice.start_date || '');
    setEditEndDate(coursePrice.end_date || '');
    setEditRequiresReview(!!coursePrice.requires_review);
    setEditRequiresFile(!!coursePrice.requires_file);
    setEditReviewInstructions(coursePrice.review_instructions || '');
    setEditActive(coursePrice.id ? !!coursePrice.active : true);
    setEditPriceOrder(coursePrice.id ? coursePrice.price_order : '');
    setEditErrors([]);
    setEditingPrice(coursePrice);
  }

  function callUpsertPrice() {
    const amount = dollarsToCents(editAmount);
    if (amount === null) {
      setEditErrors([{message: 'The amount must be a number, eg. 49.99', fields: ['amount']}]);
      return;
    }
    // A file is only looked at during the review, so it can't be required without one
    const requiresFile = editRequiresReview && editRequiresFile;
    upsertCoursePrice(editingPrice?.id, course.id, editName, editDescription, amount, editAccessType,
      editAccessDays === '' ? null : editAccessDays, editAccessEndDate, editStartDate, editEndDate, editRequiresReview, requiresFile, editRequiresReview ? editReviewInstructions : '', editActive,
      editPriceOrder === '' ? null : editPriceOrder,
      (code, data, newErrors) => {
        if (code === 200) {
          showSuccessToast(isAdding ? 'Added price.' : 'Updated price.');
          setPriceList(data);
          setEditingPrice(null);
          setEditErrors([]);
        } else {
          setEditErrors(newErrors);
        }
      });
  }

  function callDeletePrice() {
    deleteCoursePrice(deletingPrice?.id, (code, data, newErrors) => {
      setDeletingPrice(null);
      if (code === 200) {
        showSuccessToast('Deleted price.');
        setPriceList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  // How long the access bought at a price lasts, as one readable string
  function getAccessStr(coursePrice) {
    if (coursePrice?.course_access_type === COURSE_ACCESS_TYPE_DAYS) {
      return `${coursePrice.access_days} days`;
    }
    if (coursePrice?.course_access_type === COURSE_ACCESS_TYPE_END_DATE) {
      return `Until ${coursePrice.access_end_date}`;
    }
    return COURSE_ACCESS_TYPE_NAMES[coursePrice?.course_access_type] || '';
  }

  // Column setup
  const colDefs = [
    {field: 'name', cellRenderer: toolTipRenderer, headerName: 'Name', flex: 3, minWidth: 150},
    {
      field: 'amount', headerName: 'Amount', flex: 1, minWidth: 100,
      valueFormatter: params => params.value === 0 ? 'Free' : centsToDollarStrWSign(params.value),
    },
    {
      headerName: 'Access', flex: 1, minWidth: 120, cellRenderer: toolTipRenderer,
      valueGetter: params => getAccessStr(params.data),
    },
    {field: 'start_date', headerName: 'Starts', flex: 1, minWidth: 110},
    {field: 'end_date', headerName: 'Ends', flex: 1, minWidth: 110},
    {
      field: 'requires_review', headerName: 'Review', flex: 1, minWidth: 100,
      headerTooltip: 'An admin must approve the purchase before access is granted',
      valueFormatter: params => params.value ? (params.data.requires_file ? 'Yes + File' : 'Yes') : '',
    },
    {
      field: 'active', headerName: 'Active', flex: 1, minWidth: 90,
      valueFormatter: params => params.value ? 'Yes' : 'No',
      cellClass: params => params.value ? '' : 'text-danger',
    },
    {field: 'price_order', headerName: 'Order', flex: 1, minWidth: 90},
    {field: 'description', cellRenderer: toolTipRenderer, headerName: 'Description', flex: 3, minWidth: 150},
    ...(canEdit ? [{
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: 150, minWidth: 150,
      cellRenderer: params => {
        return <Fragment>
          <Button type="button" size="sm" className="me-2" onClick={() => startEditingPrice(params.data)}>
            Edit
          </Button>
          <Button type="button" variant="danger" size="sm" onClick={() => setDeletingPrice(params.data)}>
            Delete
          </Button>
        </Fragment>;
      }
    }] : []),
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  // A course which has not been saved yet has no id, so there is nowhere to put a price
  if (!course?.id) {
    return (
      <Container fluid className="usacm-container-wide">
        <Row>
          <Col className="text-center mt-3">
            Save this {courseLabel.toLowerCase()} before adding prices.
          </Col>
        </Row>
        <Row>
          <Col className="usacm-button-row mt-3">
            <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="usacm-container-wide">

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={priceList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          {canEdit &&
            <Button type="button" onClick={() => startEditingPrice({})}>Add Price</Button>
          }
        </Col>
      </Row>

      <Modal show={!!editingPrice}
             onHide={() => setEditingPrice(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isAdding ? 'Add Price' : 'Edit Price'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="mb-3">
            <Col className="col-8">
              <Form.Group controlId="edit_price_name">
                <FloatingLabel controlId="edit_price_name" label="Name">
                  <Form.Control type="text"
                                placeholder="Name"
                                name="name"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'name')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'name')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>Shown in the catalog, eg. Member, Non-Member, Student</Form.Text>
              </Form.Group>
            </Col>
            <Col className="col-4">
              <Form.Group controlId="edit_price_amount">
                <FloatingLabel controlId="edit_price_amount" label="Amount ($)">
                  <Form.Control type="number"
                                step="0.01"
                                min="0"
                                placeholder="Amount"
                                name="amount"
                                value={editAmount}
                                onChange={e => setEditAmount(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'amount')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'amount')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>0 is free - users enroll without paying</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group controlId="edit_price_description">
                <FloatingLabel controlId="edit_price_description" label="Description (optional)">
                  <Form.Control as="textarea"
                                placeholder="Description"
                                name="description"
                                style={{height: '80px'}}
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'description')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'description')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col className="col-6">
              <Form.Group controlId="edit_price_course_access_type">
                <FloatingLabel controlId="edit_price_course_access_type" label="Access Lasts">
                  <Form.Control
                    className="form-select"
                    as="select"
                    value={editAccessType}
                    onChange={e => setEditAccessType(e.target.value)}
                    isInvalid={fieldHasErrors(editErrors, 'course_access_type')}
                  >
                    {COURSE_ACCESS_TYPES.map(accessType =>
                      <option value={accessType} key={accessType}>{COURSE_ACCESS_TYPE_NAMES[accessType]}</option>)}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'course_access_type')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="col-6">
              {editAccessType === COURSE_ACCESS_TYPE_DAYS &&
                <Form.Group controlId="edit_price_access_days">
                  <FloatingLabel controlId="edit_price_access_days" label="Days of Access">
                    <Form.Control type="number"
                                  step="1"
                                  min="1"
                                  placeholder="Days of Access"
                                  name="access_days"
                                  value={editAccessDays}
                                  onChange={e => setEditAccessDays(e.target.value)}
                                  isInvalid={fieldHasErrors(editErrors, 'access_days')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(editErrors, 'access_days')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                  <Form.Text muted>Counted from when access is granted</Form.Text>
                </Form.Group>
              }
              {editAccessType === COURSE_ACCESS_TYPE_END_DATE &&
                <Form.Group controlId="edit_price_access_end_date">
                  <FloatingLabel controlId="edit_price_access_end_date" label="Access Ends On">
                    <Form.Control type="date"
                                  placeholder="Access Ends On"
                                  name="access_end_date"
                                  value={editAccessEndDate}
                                  onChange={e => setEditAccessEndDate(e.target.value)}
                                  isInvalid={fieldHasErrors(editErrors, 'access_end_date')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(editErrors, 'access_end_date')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              }
            </Col>
          </Row>

          <Row className="mb-3">
            <Col className="col-6">
              <Form.Group controlId="edit_price_start_date">
                <FloatingLabel controlId="edit_price_start_date" label="Available From (optional)">
                  <Form.Control type="date"
                                placeholder="Available From"
                                name="start_date"
                                value={editStartDate}
                                onChange={e => setEditStartDate(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'start_date')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'start_date')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="col-6">
              <Form.Group controlId="edit_price_end_date">
                <FloatingLabel controlId="edit_price_end_date" label="Available Until (optional)">
                  <Form.Control type="date"
                                placeholder="Available Until"
                                name="end_date"
                                value={editEndDate}
                                onChange={e => setEditEndDate(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'end_date')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'end_date')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>
              <Form.Check
                type="checkbox"
                label="Requires Review - an admin must approve the purchase before access is granted"
                id="edit_price_requires_review"
                checked={editRequiresReview}
                onChange={e => setEditRequiresReview(e.target.checked)}
              />
            </Col>
          </Row>

          {editRequiresReview &&
            <Fragment>
              <Row className="mb-3">
                <Col>
                  <Form.Check
                    type="checkbox"
                    label="Requires File - the buyer uploads proof at checkout for the review"
                    id="edit_price_requires_file"
                    checked={editRequiresFile}
                    onChange={e => setEditRequiresFile(e.target.checked)}
                    isInvalid={fieldHasErrors(editErrors, 'requires_file')}
                  />
                  <div className="usacm-invalid-feedback">
                    {getErrorMessageForField(editErrors, 'requires_file')}
                  </div>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group controlId="edit_price_review_instructions">
                    <FloatingLabel controlId="edit_price_review_instructions" label="Review Instructions (optional)">
                      <Form.Control as="textarea"
                                    placeholder="Review Instructions"
                                    name="review_instructions"
                                    style={{height: '80px'}}
                                    value={editReviewInstructions}
                                    onChange={e => setEditReviewInstructions(e.target.value)}
                                    isInvalid={fieldHasErrors(editErrors, 'review_instructions')}/>
                      <Form.Control.Feedback type="invalid">
                        {getErrorMessageForField(editErrors, 'review_instructions')}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                    <Form.Text muted>Shown to the buyer, eg. "Upload a photo of your student ID"</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Fragment>
          }

          <Row className="mb-3">
            <Col className="col-4">
              <Form.Group controlId="edit_price_price_order">
                <FloatingLabel controlId="edit_price_price_order" label="Order">
                  <Form.Control type="number"
                                step="1"
                                placeholder="Order"
                                name="price_order"
                                value={editPriceOrder}
                                onChange={e => setEditPriceOrder(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'price_order')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'price_order')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                {isAdding && <Form.Text muted>Leave empty to add it to the end</Form.Text>}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>
              <Form.Check
                type="checkbox"
                label="Active - offered to buyers (uncheck to retire a price which is on orders)"
                id="edit_price_active"
                checked={editActive}
                onChange={e => setEditActive(e.target.checked)}
              />
            </Col>
          </Row>

          {getErrorMessageForField(editErrors, '') &&
            <Row>
              <Col className="text-center mt-3 usacm-error-message">
                {getErrorMessageForField(editErrors, '')}
              </Col>
            </Row>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingPrice(null)}>Cancel</Button>
          <Button variant="primary"
                  disabled={!editName.trim() || editAmount === ''}
                  onClick={() => callUpsertPrice()}>
            {isAdding ? 'Add Price' : 'Update Price'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deletingPrice}
             onHide={() => setDeletingPrice(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the price "{deletingPrice?.name}"
            ({centsToDollarStrWSign(deletingPrice?.amount)}) from this {courseLabel.toLowerCase()}?
          </p>
          <p className="mb-0">
            A price which is already on an order can't be deleted - uncheck Active on it instead.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingPrice(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => callDeletePrice()}>Delete Price</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
